import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { paylinksApi } from '../../api/endpoints/paylinks';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Alert';
import type { PaylinkFormHandle, PaylinkFormPrefill } from '../../contexts/PaylinkFormContext';

interface PaylinkFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormState {
  title: string;
  amount: string;
  currency: string;
  description: string;
}

const EMPTY: FormState = { title: '', amount: '', currency: '', description: '' };

function numOrUndef(v: string): number | undefined {
  if (!v.trim()) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export const PaylinkFormModal = forwardRef<PaylinkFormHandle, PaylinkFormModalProps>(function PaylinkFormModal({ isOpen, onClose }, ref) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  // Reset on close (falling edge), not on open — see CouponFormModal note.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setForm(EMPTY);
      setError(null);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useImperativeHandle(ref, (): PaylinkFormHandle => ({
    setFields: (values: PaylinkFormPrefill) => {
      setForm((prev) => ({
        ...prev,
        ...(values.title !== undefined ? { title: values.title } : {}),
        ...(values.amount !== undefined ? { amount: String(values.amount) } : {}),
        ...(values.currency !== undefined ? { currency: values.currency } : {}),
        ...(values.description !== undefined ? { description: values.description } : {}),
      }));
    },
  }), []);

  const createMutation = useMutation({
    mutationFn: () => paylinksApi.create(storeId!, {
      title: form.title.trim(),
      amount: numOrUndef(form.amount),
      currency: form.currency.trim() || undefined,
      description: form.description.trim() || undefined,
      products: {},
    } as never),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['paylinks', storeId] });
      toast('success', t('paylinks.createSuccess', 'Paylink created'));
      const linkId = result.link || result.paylinkId;
      if (linkId && navigator.clipboard) {
        const url = `https://${storeId}.00stores.com/paylink/${linkId}`;
        navigator.clipboard.writeText(url).catch(() => {});
        toast('info', t('paylinks.linkCopied', 'Link copied to clipboard'));
      }
      onClose();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : t('common.error', 'Something went wrong');
      setError(msg);
    },
  });

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.title.trim()) {
      setError(t('paylinks.titleRequired', 'Title is required'));
      return;
    }
    createMutation.mutate();
  }, [form.title, createMutation, t]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('paylinks.create', 'Create paylink')}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} type="button">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending} type="submit" form="paylink-create-form">
            {createMutation.isPending ? t('common.saving', 'Saving...') : t('common.create', 'Create')}
          </Button>
        </div>
      }
    >
      <form id="paylink-create-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          id="title"
          label={t('paylinks.title', 'Title')}
          required
          value={form.title}
          onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            id="amount"
            label={t('paylinks.amount', 'Amount')}
            type="number"
            step="0.01"
            value={form.amount}
            onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))}
          />
          <Input
            id="currency"
            label={t('paylinks.currency', 'Currency (e.g. SAR)')}
            value={form.currency}
            onChange={(e) => setForm((s) => ({ ...s, currency: e.target.value.toUpperCase() }))}
          />
        </div>
        <Textarea
          id="description"
          label={t('paylinks.description', 'Description')}
          rows={2}
          value={form.description}
          onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
});
